import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { PieChart, Pie, Legend, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import './SurveyCard.css';

const API = process.env.REACT_APP_API_BASE_URL;

const SurveyCard = () => {
    const { user } = useSelector((state) => state.authReducer.authData);
    const [survey, setSurvey] = useState(null);
    const [hasResponded, setHasResponded] = useState(false);
    const [results, setResults] = useState(null);
    const [selected, setSelected] = useState([]);
    const [showResults, setShowResults] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const [loading, setLoading] = useState(false);
    const pollIntervalRef = useRef(null);
    const dismissedRef = useRef(dismissed);
    const selectedRef = useRef(selected);

    useEffect(() => { dismissedRef.current = dismissed; }, [dismissed]);
    useEffect(() => { selectedRef.current = selected; }, [selected]);

    const [expanded, setExpanded] = useState(false);
    const isMobile = window.innerWidth < 768;

    const fetchSurveyRef = useRef(null);
    const userIdRef = useRef(user._id);
    useEffect(() => { userIdRef.current = user._id; }, [user._id]);

    const fetchResults = useCallback(async (surveyId) => {
        try {
            const res = await fetch(`${API}/survey/results/${surveyId}`, { credentials: 'include' });
            const data = await res.json();
            setResults(data);
        } catch (error) {
            console.error('Results fetch error:', error);
        }
    }, []);
    const fetchResultsRef = useRef(fetchResults);

    const fetchSurvey = useCallback(async () => {
        try {
            const res = await fetch(`${API}/survey/active`, { credentials: 'include' });
            const data = await res.json();

            if (!data) {
                setSurvey(null);
                setHasResponded(false);
                setResults(null);
                setShowResults(false);
                setDismissed(false);
                return;
            }

            setSurvey(prev => {
                if (prev?._id !== data._id) {
                    setSelected([]);
                    setShowResults(false);
                    setHasResponded(false);
                    setResults(null);
                    setDismissed(false);
                }
                return data;
            });

            const checkRes = await fetch(`${API}/survey/check/${data._id}/${userIdRef.current}`, { credentials: 'include' });
            const checkData = await checkRes.json();

            if (checkData.hasResponded) {
                setHasResponded(true);
                setShowResults(true);
                await fetchResults(data._id);
            }

        } catch (error) {
            console.error('Survey fetch error:', error);
        }
    }, []);

    useEffect(() => {
        fetchSurveyRef.current = fetchSurvey;
        fetchResultsRef.current = fetchResults;
    }, [fetchSurvey, fetchResults]);
    
    useEffect(() => {
        const timeout = setTimeout(() => {
            fetchSurveyRef.current?.();
        }, 100);
    
        pollIntervalRef.current = setInterval(() => {
            fetchSurveyRef.current?.();
        }, 30000);
    
        return () => {
            clearTimeout(timeout);
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        };
    }, []);
    
    const handleSelect = (index) => {
        if (survey.template === 1) {
            setSelected([index]);
        } else {
            setSelected(prev =>
                prev.includes(index)
                    ? prev.filter(i => i !== index)
                    : [...prev, index]
            );
        }
    };

    const handleSubmit = async () => {
        if (selected.length === 0) return;
        setLoading(true);
        try {
            await fetch(`${API}/survey/respond`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ surveyId: survey._id, userId: user._id, answers: selected })
            });
            await fetchResults(survey._id);
            setHasResponded(true);
            setShowResults(true);
        } catch (error) {
            console.error('Submit error:', error);
        }
        setLoading(false);
    };

    if (!survey || dismissed) return null;
    if (hasResponded && !showResults) return null;

    const renderTemplate1Results = () => {
        if (!results || !results.counts) return (
            <div style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', padding: 12 }}>
                Loading results...
            </div>
        );
    
        const chartData = results.counts.map(c => ({
            name: c.option,
            value: c.count,
            fill: c.color
        }));
    
        const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
            if (percent < 0.08) return null;
    
            const RADIAN = Math.PI / 180;
            const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
            const x = cx + radius * Math.cos(-midAngle * RADIAN);
            const y = cy + radius * Math.sin(-midAngle * RADIAN);
    
            return (
                <text
                    x={x}
                    y={y}
                    fill="white"
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={11}
                    fontWeight={700}
                >
                    {`${(percent * 100).toFixed(0)}%`}
                </text>
            );
        };
    
        return (
            <div style={{ width: '100%' }}>
                <ResponsiveContainer width="100%" height={160}>
                    <PieChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                        <Pie
                            data={chartData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={30}
                            outerRadius={60}
                            label={renderCustomLabel}
                            labelLine={false}
                        />
                        <Tooltip formatter={(value, name) => [`${value} votes`, name]} />
                    </PieChart>
                </ResponsiveContainer>
    
                <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: '4px 10px', 
                    justifyContent: 'center', 
                    padding: '4px 8px' 
                }}>
                    {chartData.map((entry, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
                            <div style={{
                                width: 8, height: 8,
                                borderRadius: '50%',
                                background: entry.fill,
                                flexShrink: 0
                            }} />
                            <span style={{ color: '#374151' }}>{entry.name}</span>
                        </div>
                    ))}
                </div>
    
                <div style={{ textAlign: 'center', fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                    {results.total} {results.total === 1 ? 'response' : 'responses'}
                </div>
            </div>
        );
    };

    const renderTemplate2Results = () => {
        if (!results || !results.counts) return (
            <div style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', padding: 12 }}>
                Loading results...
            </div>
        );
        const chartData = results.counts.map(c => ({
            name: c.option,
            value: c.count,
            fill: c.color
        }));
    
        return (
            <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(value) => [`${value} votes`]} />
                    <Bar dataKey="value" />
                </BarChart>
            </ResponsiveContainer>
        );
    }

    const cardContent = (
        <div className={`survey-card ${isMobile ? 'survey-mobile-expanded' : ''}`}>
            {showResults && (
                <button className="survey-dismiss" onClick={() => setDismissed(true)}>✕</button>
            )}

            {!showResults ? (
                <>
                    <div className="survey-question">{survey.question}</div>
                    <div className="survey-options">
                        {survey.options.map((opt, i) => (
                            <div
                                key={i}
                                className={`survey-option ${selected.includes(i) ? 'selected' : ''}`}
                                style={{ borderColor: selected.includes(i) ? opt.color : '#ddd' }}
                                onClick={() => handleSelect(i)}
                            >
                                {survey.template === 2 && (
                                    <input
                                        type="checkbox"
                                        checked={selected.includes(i)}
                                        readOnly
                                        style={{ marginRight: 8 }}
                                    />
                                )}
                                {opt.text}
                            </div>
                        ))}
                    </div>
                    <div className="survey-hint">Answer the survey to see what others are saying!</div>
                    <button
                        className="survey-submit button"
                        onClick={handleSubmit}
                        disabled={loading || selected.length === 0}
                    >
                        {loading ? 'Submitting...' : 'Submit'}
                    </button>
                </>
            ) : (
                <>
                    <div className="survey-question">{survey.question}</div>
                    <div className="survey-results-label">
                        {results ? `${results.total} ${results.total === 1 ? 'response' : 'responses'}` : 'Loading...'}
                    </div>
                    {survey.template === 1 && renderTemplate1Results()}
                    {survey.template === 2 && renderTemplate2Results()}
                </>
            )}
        </div>
    );

    if (isMobile) {
        return expanded ? (
            <div className="survey-mobile-container">
                <button className="survey-mobile-close" onClick={() => setExpanded(false)}>✕</button>
                {cardContent}
            </div>
        ) : (
            <div className="survey-mobile-bubble" onClick={() => setExpanded(true)}>
                📊
            </div>
        );
    }

    return cardContent;
};

export default SurveyCard;